import React, { useCallback, useRef, useState } from "react";
import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Spinner } from "react-bootstrap";

import ComparatorSearcher from "./ComparatorSearcher";
import ComparatorResults from "./ComparatorResults";

import { formatDecimal } from "../../helpers/format";
import { productServices } from "../../api/services/products";
import { useSearchParams } from "../../context/SearchParamsContext";

import moment from 'moment';
import 'moment/locale/es'; 

const ComparatorPage = () => {

    const { comparatorHistoricalRange } = useSearchParams();
    const [ comparatorSlot1, setComparatorSlot1 ] = useState(null);
    const [ comparatorSlot2, setComparatorSlot2 ] = useState(null);
    const [ comparatorSlot3, setComparatorSlot3 ] = useState(null);
    const [ comparatorSlot4, setComparatorSlot4 ] = useState(null);
    const [ userSelectedSlot, setUserSelectedSlot ] = useState(0);

    const [ currentDates, setCurrentDates ] = useState({ minDate: null, maxDate: null });

    const [isLoading, setIsLoading] = useState(true);
    const [isError, setIsError] = useState(false);
    const navigate = useNavigate();
    const { productId } = useParams();
    const dateFormatRent = "YYYY-MM-DD";
    const [productType, setProductType] = useState();

    useEffect(() => {
        if (productId) {
            setIsLoading(true);
            fetchDetailData();
        } else {
            setIsError(true);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [productId]);

    const getUpdatedSlotOnHistoricalRangeChange = async (item) => {
        if (!item) return null;
        const id = item.title.id;
        const type = item.title.porductType;
        const rentability = await getRentabilityUsingHistoricalData(id, type);
        const updatedItem = {
            ...item,
            resumen: {
                ...item?.resumen,
                rentabilidadPeriodo: rentability
            }
        };
        return updatedItem;
    };

    const updateRentabilityForAllSlots = async () => {
        if (comparatorSlot1) setComparatorSlot1(await getUpdatedSlotOnHistoricalRangeChange(comparatorSlot1));
        if (comparatorSlot2) setComparatorSlot2(await getUpdatedSlotOnHistoricalRangeChange(comparatorSlot2));
        if (comparatorSlot3) setComparatorSlot3(await getUpdatedSlotOnHistoricalRangeChange(comparatorSlot3));
        if (comparatorSlot4) setComparatorSlot4(await getUpdatedSlotOnHistoricalRangeChange(comparatorSlot4));
    };

    useEffect(() => {
        updateRentabilityForAllSlots();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentDates]);

    useEffect(() => {
        if (!comparatorSlot1) return;
        if (comparatorHistoricalRange && comparatorHistoricalRange.length > 0) {
            setCurrentDates({ minDate: comparatorHistoricalRange?.[0].format("YYYY-MM-DD 00:00:00"), maxDate: comparatorHistoricalRange?.[1].format("YYYY-MM-DD 00:00:00") });
        } else {
            setCurrentDates({ minDate: null, maxDate: null });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [comparatorHistoricalRange]);

    const fetchDetailData = async () => {
        try {
            const productDetail = await productServices.getProductDetail(productId);
            productDetail.code !== "200" && setIsError(true);
            setProductType(productDetail?.response?.detalleproducto.tipo);

            setComparatorSlot(productDetail?.response?.detalleproducto);
        } catch (error) {
            window.scrollTo(0, 0);
            setIsError(true);
        } finally {
            setIsLoading(false);
        }
    };

    const comparatorPage = useRef(null);

    const formFieldFocusEvent = new Event("formFieldFocusEvent");

    const clickAddComparatorButton = (comparatorSlot) => {
        setUserSelectedSlot(comparatorSlot);
        comparatorPage?.current?.dispatchEvent(formFieldFocusEvent);
    }

    const getFirstEmptySlot = () => {
        if (comparatorSlot1 === null) return 1;
        if (comparatorSlot2 === null) return 2;
        if (comparatorSlot3 === null) return 3;
        return 4;
    };

    const getMinMaxDateFromHistoricalData = (data) => {
        if (data.length === 0) return;

        let minDate = new Date(data[0]?.fecha);
        let maxDate = new Date(data[0]?.fecha);

        let minDateIndex = 0;
        let maxDateIndex = 0;

        data.forEach((item, index) => {
            const currentDate = new Date(item?.fecha);

            if (currentDate < minDate) {
                minDate = currentDate;
                minDateIndex = index;
            }

            if (currentDate > maxDate) {
                maxDate = currentDate;
                maxDateIndex = index;
            }
        });

        return { minDate: data[minDateIndex]?.fecha, maxDate: data[maxDateIndex]?.fecha};
    };

    const updateGlobalDatesFromHistorical = useCallback(async (productId) => {
        if (currentDates.minDate !== null && currentDates.maxDate !== null) return;
        const productHistorical = await productServices.getHistorical(productId);
        productHistorical.code !== "200" && setIsError(true);

        const { minDate, maxDate } = getMinMaxDateFromHistoricalData(productHistorical?.response);

        if (currentDates.minDate === null || currentDates.maxDate === null) {
            setCurrentDates(prevState => ({ ...prevState, maxDate, minDate }));
        } else {
            const minDateObject = new Date(minDate);
            const maxDateObject = new Date(maxDate);

            const globalMinDateObject = new Date(currentDates.minDate);
            const globalMaxDateObject = new Date(currentDates.maxDate);

            if (minDateObject < globalMinDateObject) {
                setCurrentDates(prevState => ({ ...prevState, minDate }));
            }

            if (maxDateObject > globalMaxDateObject) {
                setCurrentDates(prevState => ({ ...prevState, maxDate }));
            }
        }
    }, [currentDates]);

    const datesDiffAreGreaterThan365Days = (date1, date2) => {
        const date1Object = new Date(date1);
        const date2Object = new Date(date2);

        return date1Object.getTime() - date2Object.getTime() > 31536000000;
    };

    const getRentabilityUsingHistoricalData = async (productId, productType) => {
        // updateGlobalDatesFromHistorical(productId);

        let { minDate, maxDate } = currentDates;

        if (currentDates.minDate === null || currentDates.maxDate === null) {
            const productHistorical = await productServices.getHistorical(productId);
            productHistorical.code !== "200" && setIsError(true);

            ({ minDate, maxDate } = getMinMaxDateFromHistoricalData(productHistorical?.response));
        }

        const isGreaterThan365Days = datesDiffAreGreaterThan365Days(maxDate, minDate);

        minDate = moment(minDate).format(dateFormatRent);
        maxDate = moment(maxDate).format(dateFormatRent);

        const rentaResume = await productServices.getRentaResume(
            productId,
            minDate,
            maxDate,
            productType
        );

        const rentability = isGreaterThan365Days ? rentaResume?.rentabilidadfechas?.tae : rentaResume?.rentabilidadfechas?.acumulado;

        return formatDecimal(rentability, 5) ?? '-';
    };

    const parseComparatorObject = (comparatorObject) => {
        return {
            title: {
                title: comparatorObject?.nombre,
                id: comparatorObject?.isin ? comparatorObject?.isin : comparatorObject?.codigoDgs,
                details: 'Detalles del fondo...',
                porductType: comparatorObject?.tipo
            },
            resumen: {
                title: 'Resumen',
                rentabilidadPeriodo: 'Calculando...',
                inversionMinima: comparatorObject?.inversionMinima,
                riesgo: comparatorObject?.riesgo
            },
            rentabilidades: {
                title: 'Rentabilidades por años',
                actualYear: comparatorObject?.ytd,
                oneYear: comparatorObject?.uno,
                threeYears: comparatorObject?.tres
            },
            histórico: {
                title: 'Histórico',
                value: 100
            }
        }
    }

    const setComparatorSlot = async (comparatorObject) => {
        const parsedComparatorObject = parseComparatorObject(comparatorObject);
        let slotToBeFilled;
        if (userSelectedSlot === 0) {
            slotToBeFilled = getFirstEmptySlot();
        } else {
            slotToBeFilled = userSelectedSlot;
        }

        const id = comparatorObject?.isin ? comparatorObject?.isin : comparatorObject?.codigoDgs;
        const type = comparatorObject?.tipo;

        updateGlobalDatesFromHistorical(id);

        const rentability = await getRentabilityUsingHistoricalData(id, type);
        switch (slotToBeFilled) {
            case 1:
                // debugger;
                setComparatorSlot1({
                    ...parsedComparatorObject,
                    resumen: {
                        ...parsedComparatorObject?.resumen,
                        rentabilidadPeriodo: rentability
                    }
                });
                break;
            case 2:
                setComparatorSlot2({
                    ...parsedComparatorObject,
                    resumen: {
                        ...parsedComparatorObject?.resumen,
                        rentabilidadPeriodo: rentability
                    }
                });
                break;
            case 3:
                setComparatorSlot3({
                    ...parsedComparatorObject,
                    resumen: {
                        ...parsedComparatorObject?.resumen,
                        rentabilidadPeriodo: rentability
                    }
                });
                break;
            case 4:
                setComparatorSlot4({
                    ...parsedComparatorObject,
                    resumen: {
                        ...parsedComparatorObject?.resumen,
                        rentabilidadPeriodo: rentability
                    }
                });
                break;
        
            default:
                setUserSelectedSlot(0);
                break;
        };

        if (comparatorSlot1 !== null && slotToBeFilled !== 1) {
            const rentabilidad = await getRentabilityUsingHistoricalData(comparatorSlot1?.title?.id, comparatorSlot1?.title?.productType);
            setComparatorSlot1({
                ...comparatorSlot1,
                resumen: {
                    ...comparatorSlot1?.resumen,
                    rentabilidadPeriodo: rentabilidad
                }
            });
        }
        if (comparatorSlot2 !== null && slotToBeFilled !== 2) {
            const rentabilidad = await getRentabilityUsingHistoricalData(comparatorSlot2?.title?.id, comparatorSlot2?.title?.productType);
            setComparatorSlot2({
                ...comparatorSlot2,
                resumen: {
                    ...comparatorSlot2?.resumen,
                    rentabilidadPeriodo: rentabilidad
                }
            });
        }
        if (comparatorSlot3 !== null && slotToBeFilled !== 3) {
            const rentabilidad = await getRentabilityUsingHistoricalData(comparatorSlot3?.title?.id, comparatorSlot3?.title?.productType);
            setComparatorSlot3({
                ...comparatorSlot3,
                resumen: {
                    ...comparatorSlot3?.resumen,
                    rentabilidadPeriodo: rentabilidad
                }
            });
        }
        if (comparatorSlot4 !== null && slotToBeFilled !== 4) {
            const rentabilidad = await getRentabilityUsingHistoricalData(comparatorSlot4?.title?.i, comparatorSlot4?.title?.productType);
            setComparatorSlot4({
            
                ...comparatorSlot4,
                resumen: {
                    ...comparatorSlot4?.resumen,
                    rentabilidadPeriodo: rentabilidad
                }
            });
        }
    };

    return (
        <div data-testid="pagina-comparador" ref={comparatorPage}>
            <section className="wrapper-search container">
                <div style={{fontWeight: 600, fontSize: 22}}>
                    <span style={{color: 'red'}}><i onClick={() => navigate(-1)} style={{cursor: 'pointer'}} className="fa-solid fa-chevron-left"></i></span> Comparador
                </div>
            </section>
            {isLoading && (
                <div className="d-flex justify-content-center align-items-center vh-100">
                    <div className="alert alert-danger" role="alert">
                        <Spinner animation="border" variant="danger" role="status" className="me-2"/>
                        Cargando...
                    </div>
                </div>
            )}
            {isError && (
                <div className="alert alert-danger mt-3" role="alert">Se ha producido un error, vuelve a intentarlo</div>
            )}
            <ComparatorSearcher
                comparatorPageRef={comparatorPage}
                setComparatorSlot={setComparatorSlot}
                />
            {
                /*
            <div className="container padding-1">
                <div className="container">
                    <p style={{marginTop: 20, marginBottom: 20}}>
                        Esta herramienta, con finalidad exclusivamente informativa, permite, por defecto, comparar la evolución y la rentabilidad de aquellos productos (fondos,planes y epsv) en comercialización con la misma categoría de inversión y perfil de riesgo. Si lo desea, además el comparador le permite consultar por tipología de producto, a través del buscador integrado en “Producto a comparar”. Como máximo podrá consultar 4 productos a la vez.
                    </p>
                </div>
            </div> */
            }
            <ComparatorResults
                comparatorSlot1={comparatorSlot1}
                comparatorSlot2={comparatorSlot2}
                comparatorSlot3={comparatorSlot3}
                comparatorSlot4={comparatorSlot4}
                clickAddComparatorButton={clickAddComparatorButton}
                />
        </div>
    );
};

export default ComparatorPage;
